import moment from 'moment-timezone';

export interface TimezoneInfo {
  timezone: string;
  offset: string;
  displayName: string;
}

export class TimezoneService {
  /**
   * Get all available timezones
   */
  public static getAvailableTimezones(): TimezoneInfo[] {
    return moment.tz.names().map(tz => ({
      timezone: tz,
      offset: moment.tz(tz).format('Z'),
      displayName: this.getTimezoneDisplayName(tz)
    }));
  }

  /**
   * Get timezone display name
   */
  public static getTimezoneDisplayName(timezone: string): string {
    const offset = moment.tz(timezone).format('Z');
    const city = timezone.split('/').pop()?.replace(/_/g, ' ') || timezone;
    return `${city} (${offset})`;
  }

  /**
   * Convert UTC time to local timezone
   */
  public static toLocal(utcTime: string, timezone: string): string {
    return moment.utc(utcTime).tz(timezone).format('YYYY-MM-DD hh:mm A');
  }

  /**
   * Get current time in specific timezone
   */
  public static getCurrentLocal(timezone: string): string {
    return moment.tz(timezone).format('YYYY-MM-DD hh:mm A');
  }

  /**
   * Convert stored UTC schedule to local time for display
   */
  public static convertScheduleToLocal(utcDateTime: string, timezone: string): { date: string; time: string } {
    const localDateTime = this.toLocal(utcDateTime, timezone);
    const [date, time] = localDateTime.split(' ');
    return { date, time };
  }

  /**
   * Format time for display in user's timezone
   */
  public static formatTimeForDisplay(utcTime: string, timezone: string, format: string = 'hh:mm A'): string {
    return moment.utc(utcTime).tz(timezone).format(format);
  }

  /**
   * Format date for display in user's timezone
   */
  public static formatDateForDisplay(utcDate: string, timezone: string, format: string = 'YYYY-MM-DD'): string {
    return moment.utc(utcDate).tz(timezone).format(format);
  }

  /**
   * Get common timezones for quick selection
   */
  public static getCommonTimezones(): TimezoneInfo[] {
    const commonTz = [
      'America/New_York',      // EST/EDT
      'America/Chicago',       // CST/CDT (Chicago, Dallas, etc.)
      'America/Denver',        // MST/MDT
      'America/Los_Angeles',   // PST/PDT
      'America/Phoenix',       // MST (no DST)
      'America/Monterrey',     // CST (Monterrey, Mexico - no DST)
      'Europe/London',         // GMT/BST
      'Europe/Paris',          // CET/CEST
      'Asia/Tokyo',            // JST
      'Asia/Shanghai',         // CST
      'Australia/Sydney',      // AEST/AEDT
      'UTC'                    // UTC
    ];

    return commonTz.map(tz => ({
      timezone: tz,
      offset: moment.tz(tz).format('Z'),
      displayName: this.getTimezoneDisplayName(tz)
    }));
  }

  /**
   * Get user's timezone from browser or default
   */
  public static getUserTimezone(): string {
    return moment.tz.guess() || 'UTC';
  }

  /**
   * Validate timezone string
   */
  public static isValidTimezone(timezone: string): boolean {
    return moment.tz.zone(timezone) !== null;
  }

  /**
   * Format time consistently across the application (HH:MM AM/PM)
   */
  public static formatTimeConsistent(utcTime: string, timezone: string): string {
    return moment.utc(utcTime).tz(timezone).format('hh:mm A');
  }

  /**
   * Format date and time consistently (MM/DD/YYYY HH:MM AM/PM)
   */
  public static formatDateTimeConsistent(utcTime: string, timezone: string): string {
    return moment.utc(utcTime).tz(timezone).format('MM/DD/YYYY hh:mm A');
  }

  /**
   * Format date and time for datetime-local input (YYYY-MM-DDTHH:mm)
   */
  public static formatForDateTimeLocal(utcTime: string, timezone: string): string {
    return moment.utc(utcTime).tz(timezone).format('YYYY-MM-DDTHH:mm');
  }

  /**
   * Convert datetime-local input value (in selected timezone) to UTC ISO string
   * This ensures the user's selected time is correctly converted to UTC for storage
   */
  public static convertDateTimeLocalToUTC(dateTimeLocal: string, timezone: string): string {
    // Parse the datetime-local value as if it's in the selected timezone
    // Format: YYYY-MM-DDTHH:mm (no timezone info)
    const momentInTimezone = moment.tz(dateTimeLocal, 'YYYY-MM-DDTHH:mm', timezone);
    // Convert to UTC
    return momentInTimezone.utc().toISOString();
  }
}
